// HQ → WhatsApp accounts: the organization's WhatsApp Business Accounts (WABAs). HQ connects an account with a
// system-user token (checked with Meta before anything is saved) or through Meta's Embedded Signup when the server
// has the Meta app settings, then puts its numbers to work as the main number or a branch's own number. Health
// (Meta review, quality, messaging limit, webhook, token) is checked every 6 hours and on demand. Tokens are stored
// encrypted and never shown again.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatDateTime } from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import BranchSelect from '../components/BranchSelect';
import { Confirm, EmptyState, Loader, Modal, PageHead, Spinner } from '../components/ui';

const STATUS = {
  connected: { label: 'Connected', tone: 'ok' },
  needs_attention: { label: 'Needs attention', tone: 'warn' },
  token_invalid: { label: 'Token not valid', tone: 'danger' },
  disconnected: { label: 'Disconnected', tone: 'muted' },
};
const QUALITY_TONE = { GREEN: 'ok', YELLOW: 'warn', RED: 'danger' };

function StatusPill({ status }) {
  const cfg = STATUS[status] || { label: status, tone: 'muted' };
  return <span className={`de-pill de-pill--sm de-pill--${cfg.tone}`}>{cfg.label}</span>;
}

function Field({ label, children, wide, hint }) {
  return (
    <div className="de-field" style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <span className="de-label">{label}</span>
      {children}
      {hint ? <span className="de-small de-muted">{hint}</span> : null}
    </div>
  );
}

function AccountBranch({ branches, value, onChange, allowMain = true }) {
  return (
    <BranchSelect
      branches={branches}
      value={value}
      onChange={onChange}
      emptyLabel={allowMain ? 'Main number (whole organization)' : ''}
      placeholder="Choose the branch…"
    />
  );
}

function ConnectModal({ branches, onClose, onSaved }) {
  const [form, setForm] = useState({ waba_id: '', access_token: '', name: '', phone_number_id: '', franchise_id: '', label: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, franchise_id: form.franchise_id ? Number(form.franchise_id) : null };
      const res = await api.connectWaAccount(body);
      if (res.check && !res.check.ok) toast(`Connected. ${res.check.message}`, { icon: '⚠️' });
      else toast.success('Connected and checked with Meta');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const ready = form.waba_id.trim() && form.access_token.trim();
  return (
    <Modal title="Connect a WhatsApp Business Account" onClose={onClose} size="wide">
      <div className="de-callout" style={{ marginBottom: 14 }}>
        In Meta Business Settings create a system user with access to the WhatsApp Business Account, and generate a token with
        whatsapp_business_messaging and whatsapp_business_management. Meta checks the token before anything is saved.
      </div>
      <div className="de-form-grid de-form-grid--2">
        <Field label="WhatsApp Business Account id *">
          <input className="de-input de-input--bordered de-mono" value={form.waba_id} onChange={set('waba_id')} placeholder="From Meta → WhatsApp Manager" />
        </Field>
        <Field label="Name">
          <input className="de-input de-input--bordered" value={form.name} onChange={set('name')} placeholder="Shown in the app (default: Meta's name)" />
        </Field>
        <Field label="Access token *" wide hint="Stored encrypted. It is never shown again.">
          <input className="de-input de-input--bordered de-mono" type="password" autoComplete="new-password" value={form.access_token} onChange={set('access_token')} />
        </Field>
        <Field label="Put one of its numbers to work now (optional)">
          <input className="de-input de-input--bordered de-mono" value={form.phone_number_id} onChange={set('phone_number_id')} placeholder="Phone number id" />
        </Field>
        <Field label="As">
          <AccountBranch branches={branches} value={form.franchise_id} onChange={(v) => setForm((f) => ({ ...f, franchise_id: v }))} />
        </Field>
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!ready || saving} onClick={save}>
          {saving ? <Spinner light /> : null}
          Connect and check
        </button>
      </div>
    </Modal>
  );
}

function TokenModal({ account, onClose, onSaved }) {
  const [token, setToken] = useState('');
  const [name, setName] = useState(account.name || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const patch = { name };
      if (token.trim()) patch.access_token = token;
      await api.updateWaAccount(account.id, patch);
      toast.success(token.trim() ? 'Token replaced and checked' : 'Saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Edit ${account.name || account.waba_id}`} onClose={onClose}>
      <Field label="Name">
        <input className="de-input de-input--bordered" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="de-mt-16" />
      <Field label="New access token" hint="Leave empty to keep the saved token. A new one is checked with Meta first.">
        <input className="de-input de-input--bordered de-mono" type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} />
      </Field>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={saving} onClick={save}>
          {saving ? <Spinner light /> : null}
          Save
        </button>
      </div>
    </Modal>
  );
}

function AttachModal({ account, number, branches, onClose, onSaved }) {
  const [franchiseId, setFranchiseId] = useState('');
  const [label, setLabel] = useState(number.verified_name || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await api.attachWaNumber(account.id, {
        phone_number_id: number.id,
        franchise_id: franchiseId ? Number(franchiseId) : null,
        label,
        display_number: number.display_number,
      });
      if (res.check && !res.check.ok) toast(`Added. ${res.check.message}`, { icon: '⚠️' });
      else toast.success(franchiseId ? 'Branch number added' : 'Main number moved to this account');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Use ${number.display_number}`} onClose={onClose}>
      <Field label="Use it as">
        <AccountBranch branches={branches} value={franchiseId} onChange={setFranchiseId} />
      </Field>
      {!franchiseId ? (
        <div className="de-callout de-mt-8">The main number serves every branch. Moving it here changes the number every main-number message goes from.</div>
      ) : null}
      <div className="de-mt-16" />
      <Field label="Name">
        <input className="de-input de-input--bordered" value={label} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={saving} onClick={save}>
          {saving ? <Spinner light /> : null}
          Use this number
        </button>
      </div>
    </Modal>
  );
}

// Meta's Embedded Signup: the Facebook SDK opens Meta's window; Meta posts the chosen account and number back,
// and the login returns a code the server exchanges for a token.
function loadFacebookSdk(appId, version) {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = () => {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(window.FB);
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Could not load Meta’s sign-up window'));
    document.body.appendChild(script);
  });
}

function EmbeddedSignupButton({ config, onDone }) {
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    let picked = {};
    const onMessage = (event) => {
      if (!String(event.origin).endsWith('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH') picked = data.data || {};
      } catch {
        /* not Meta's message */
      }
    };
    window.addEventListener('message', onMessage);
    try {
      const FB = await loadFacebookSdk(config.app_id, config.graph_version);
      FB.login(
        (response) => {
          window.removeEventListener('message', onMessage);
          const code = response?.authResponse?.code;
          if (!code || !picked.waba_id) {
            setBusy(false);
            toast.error('Sign-up was not finished');
            return;
          }
          api
            .embeddedSignup({ code, waba_id: picked.waba_id, phone_number_id: picked.phone_number_id || '' })
            .then(() => {
              toast.success('WhatsApp account connected');
              onDone();
            })
            .catch((e) => toast.error(e.message))
            .finally(() => setBusy(false));
        },
        { config_id: config.config_id, response_type: 'code', override_default_response_type: true, extras: { sessionInfoVersion: 3 } },
      );
    } catch (e) {
      window.removeEventListener('message', onMessage);
      setBusy(false);
      toast.error(e.message);
    }
  };
  return (
    <button type="button" className="de-btn de-btn--outline" onClick={start} disabled={busy}>
      {busy ? <Spinner /> : null}
      Connect with Meta
    </button>
  );
}

function AccountCard({ account, branches, editable, onChanged }) {
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [attaching, setAttaching] = useState(null);
  const [removing, setRemoving] = useState(false);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      if (r && r.ok === false) toast.error(r.message || 'Check found problems');
      else toast.success(ok);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="de-card" style={{ marginBottom: 16 }}>
      <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="de-td-strong" style={{ fontSize: 16 }}>
            {account.name || account.waba_id} <StatusPill status={account.status} />
          </div>
          {editable ? (
            <div className="de-small de-muted de-mono">
              WABA {account.waba_id}
              {account.business_id ? ` · Business ${account.business_id}` : ''}
            </div>
          ) : null}
        </div>
        {editable ? (
          <div className="de-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={busy === 'check'} onClick={() => act('check', () => api.checkWaAccount(account.id), 'Checked with Meta')}>
              {busy === 'check' ? <Spinner /> : null}
              Check now
            </button>
            <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setEditing(true)}>
              Rename / replace token
            </button>
            {account.status !== 'disconnected' ? (
              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setRemoving(true)}>
                Disconnect
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {editable ? (
        <div className="de-form-grid de-form-grid--2 de-mt-16" style={{ gap: 8 }}>
          <div className="de-small">
            <span className="de-muted">Meta review: </span>
            {account.review_status || '—'}
          </div>
          <div className="de-small">
            <span className="de-muted">Webhook: </span>
            {account.webhook_subscribed ? <span className="de-ok-text">Subscribed</span> : <span className="de-danger-text">Not subscribed</span>}
          </div>
          <div className="de-small">
            <span className="de-muted">Token: </span>
            {account.token || '—'} ({account.token_source === 'embedded_signup' ? 'Embedded Signup' : account.token_source === 'env' ? 'moved from .env' : 'pasted'})
            {account.token_expires_at ? ` · expires ${formatDateTime(account.token_expires_at)}` : ''}
            {account.token_days_left != null && account.token_days_left <= 7 ? (
              <span className="de-danger-text" style={{ marginLeft: 6, fontWeight: 600 }}>
                {account.token_days_left < 0 ? 'Token expired' : `Token expires in ${account.token_days_left} day${account.token_days_left === 1 ? '' : 's'}`}
              </span>
            ) : null}
          </div>
          <div className="de-small">
            <span className="de-muted">Last check: </span>
            {account.last_check_at ? formatDateTime(account.last_check_at) : '—'}
          </div>
          {account.last_check_result ? (
            <div className="de-small de-muted" style={{ gridColumn: '1 / -1' }}>
              {account.last_check_result}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="de-section-label de-mt-16">Numbers in use</div>
      {account.numbers.length ? (
        <div className="de-table-wrap">
          <table className="de-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Serves</th>
                <th>Quality</th>
                <th>Messaging limit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {account.numbers.map((n) => (
                <tr key={n.channel_id}>
                  <td className="de-td-strong">
                    {n.label}
                    <div className="de-small de-muted de-mono">{n.display_number}</div>
                  </td>
                  <td>{n.branch_name}</td>
                  <td>{n.quality_rating ? <span className={`de-pill de-pill--sm de-pill--${QUALITY_TONE[n.quality_rating] || 'muted'}`}>{n.quality_rating}</span> : '—'}</td>
                  <td className="de-small">{n.messaging_limit_tier || <span className="de-muted">Not shared by Meta</span>}</td>
                  <td className="de-small">{n.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="de-small de-muted">None yet.</div>
      )}

      {editable && account.unused_numbers?.length ? (
        <>
          <div className="de-section-label de-mt-16">Not used yet</div>
          <div className="de-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {account.unused_numbers.map((n) => (
              <button key={n.id} type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setAttaching(n)} disabled={account.status === 'disconnected'}>
                {n.display_number} · {n.verified_name || 'number'} — use it
              </button>
            ))}
          </div>
        </>
      ) : null}

      {editing ? (
        <TokenModal
          account={account}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      ) : null}
      {attaching ? (
        <AttachModal
          account={account}
          number={attaching}
          branches={branches}
          onClose={() => setAttaching(null)}
          onSaved={() => {
            setAttaching(null);
            onChanged();
          }}
        />
      ) : null}
      {removing ? (
        <Confirm
          title="Disconnect this account?"
          message="Its token is deleted and nothing sends with it. This is refused while the main number or an active branch number uses it."
          confirmLabel="Disconnect"
          busy={busy === 'remove'}
          onCancel={() => setRemoving(false)}
          onConfirm={() => act('remove', () => api.removeWaAccount(account.id), 'Disconnected').finally(() => setRemoving(false))}
        />
      ) : null}
    </div>
  );
}

export default function WaAccounts() {
  const { branches, canManage } = useEngagementScope();
  const [data, setData] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = () =>
    api
      .listWaAccounts()
      .then(setData)
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
  }, []);

  if (!data) return <Loader />;
  const editable = canManage && data.can_edit;
  const attention = data.accounts.filter((a) => a.needs_attention).length;
  return (
    <div className="de-fade">
      <PageHead
        title="WhatsApp accounts"
        sub="The WhatsApp Business Accounts your numbers are on. Messages, templates and webhooks use each account's own token."
        action={
          editable ? (
            <div className="de-row" style={{ gap: 8 }}>
              {data.embedded_signup?.enabled ? <EmbeddedSignupButton config={data.embedded_signup} onDone={load} /> : null}
              <button type="button" className="de-btn de-btn--primary" onClick={() => setConnecting(true)}>
                + Connect account
              </button>
            </div>
          ) : null
        }
      />
      {attention ? (
        <div className="de-callout" style={{ marginBottom: 14 }}>
          {attention} account{attention > 1 ? 's need' : ' needs'} attention. Open it below to see what Meta reported.
        </div>
      ) : null}
      {data.accounts.length ? (
        data.accounts.map((a) => <AccountCard key={a.id} account={a} branches={branches} editable={editable} onChanged={load} />)
      ) : (
        <EmptyState icon="📱" title="No WhatsApp account connected" body="Connect your WhatsApp Business Account to send and receive messages." />
      )}
      {connecting ? (
        <ConnectModal
          branches={branches}
          onClose={() => setConnecting(false)}
          onSaved={() => {
            setConnecting(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
